/**
 * Render a result according to its spec.
 *
 * The single renderer used by BOTH the builder preview and a placed dashboard
 * tile. That is deliberate: separate paths are how a builder ends up previewing
 * something different from what it saves.
 */
import type {
  PivotView,
  QueryPlan,
  QueryResult,
  ReportModel,
  ReportSpec,
} from '@clockwise/semantic-core';
import {
  cellCoordinate,
  findMeasure,
  grainOfDimension,
  isComplete,
  pivot,
  selectMetricPeriods,
  showAsFor,
} from '@clockwise/semantic-core';
import { FormulaIcon } from '@clockwise/ui';
import { useMemo, useRef } from 'react';
import { useChartAdapter } from './chart-adapter-context';
import { type ChartSpec, pivotToChartSpec } from './chart-spec';
import { DetailTable } from './detail-table';
import { chartDrillCell, coordinateLabels, type OnDrill } from './drill';
import { type MetricComparison, MetricValue, readablePeriod } from './metric-value';
import { PivotTable } from './pivot-table';

export interface ReportViewProps {
  model: ReportModel;
  spec: ReportSpec;
  result: QueryResult;
  /** Which axes this projection is responsible for ordering. */
  plan?: QueryPlan;
  /**
   * Called when a header is clicked. OMIT to make headers inert — a placed
   * dashboard tile must not drift from the order it was saved with.
   *
   * A listing has one axis, and it is the rows: `'cols'` never arrives from it.
   */
  onSort?: (axis: 'rows' | 'cols', by: string, at?: readonly string[], on?: string) => void;
  /**
   * Fold or unfold one pivot group, and every group at one level. OMIT both
   * and the table draws no fold controls — the spec's folds still apply, for
   * the same reason `onSort` is omitted on a placed tile.
   */
  onToggleGroup?: (key: string) => void;
  onCollapseLevel?: (dimensionId: string, collapsed: boolean) => void;
  /**
   * A figure was clicked. OMIT and nothing is clickable.
   *
   * A pivot table's cell, a chart's mark and a metric's number all report the
   * SAME `DrillCell` — the coordinate, the measure and the figure — so a host
   * drills them one way. A listing is already the rows and never calls it.
   */
  onDrill?: OnDrill;
  /**
   * The prior window's value, for a `metric`. See {@link MetricValue}.
   *
   * Ignored by every other viz — a comparison needs one number to compare.
   */
  previous?: number | null;
  /**
   * The figure over the reader's WHOLE window and over its mirror, for a
   * trended `metric` — see {@link MetricComparison}.
   *
   * Given, it replaces the one-grain pick: the figure is the window, the
   * comparison is its mirror, and the sparkline runs uncut through both.
   * Absent (the builder preview), the metric reports one grain as before.
   */
  comparison?: MetricComparison;
  /**
   * The window the READER chose, when the spec's own has been widened.
   *
   * A metric tile queries further back than the dashboard asks, so its
   * sparkline has history — which means `spec.from`/`to` no longer say what
   * period the reader is looking at. That distinction is what tells "show me
   * September" apart from "show me the year, which happens to contain
   * September". Defaults to the spec's own window, which is correct
   * everywhere the window was not widened.
   */
  selectedPeriod?: { from: string; to: string };
  /**
   * What the host's own chrome already calls this, when it has a header.
   *
   * Forwarded to {@link MetricValue}, which drops its label rather than
   * printing the measure's name under a card that has just said it. Ignored by
   * every other viz — nothing else in here names itself.
   */
  heading?: string;
  /** Forwarded to {@link MetricValue}: the host names the measure elsewhere. */
  measureNamed?: boolean;
  /** Forwarded to {@link MetricValue}: the host shows the window reported. */
  periodNamed?: boolean;
  /** Injectable for tests; defaults to now. */
  today?: Date;
}

export function ReportView({
  model,
  spec,
  result,
  plan,
  onSort,
  onToggleGroup,
  onCollapseLevel,
  onDrill,
  previous,
  comparison,
  selectedPeriod,
  heading,
  measureNamed,
  periodNamed,
  today,
}: ReportViewProps) {
  const detail = spec.kind === 'detail';
  // Unconditionally, because a hook cannot be otherwise. Null when the host
  // provided no adapter, which the chart branch below degrades on.
  const adapter = useChartAdapter();
  // Not conditionally: a hook cannot be. `pivot()` over a listing's result is
  // cheap and unread — the projection it builds has no axes to build from.
  const view = useMemo(
    () => pivot(model, spec, result, plan?.memory.sort, plan?.memory.limit),
    [model, spec, result, plan],
  );
  // Where a chart was last pressed, relative to the chart, so the drill menu
  // opens beside the mark clicked rather than at the chart's corner: a chart
  // reports WHICH point was clicked, not where on the screen it is.
  const chartBox = useRef<HTMLDivElement>(null);
  const pressedAt = useRef<{ x: number; y: number } | null>(null);

  if (result.rows.length === 0) {
    // A listing at an offset can be empty because the PAGE is past the end
    // rather than because nothing matched — telling a reader their filters
    // found nothing when the report has 12,000 rows sends them to fix the
    // wrong thing. The builder returns them to the first page; this is what
    // they read on the way.
    const pastTheEnd = detail && (spec.offset ?? 0) > 0;
    return (
      <p className="rk-empty">
        {pastTheEnd
          ? 'This page is past the end of the report.'
          : 'No rows matched this combination.'}
      </p>
    );
  }

  if (detail) {
    return (
      <DetailTable
        model={model}
        spec={spec}
        rows={result.rows}
        totals={result.meta.totals}
        paged={result.meta.truncated || (spec.offset ?? 0) > 0}
        onSort={onSort && ((by) => onSort('rows', by))}
        formulas={result.meta.formulas}
      />
    );
  }

  // A number, and — when the spec trends over time — its history beside it.
  //
  // The NUMBER never needs a chart library, so a KPI renders whether or not a
  // host provided an adapter. The sparkline does, so it is drawn only when one
  // is there; without it the tile degrades to the value and its delta, which
  // is the part that carries the meaning anyway.
  if (spec.viz === 'metric') {
    const measureId = spec.measures[0];
    const trended = spec.rows.length > 0 && measureId !== undefined;
    const series = trended
      ? view.rowKeys.map((rowKey) => view.cell(rowKey, [], measureId as string))
      : undefined;
    // The period keys, for naming what is reported and for deciding which
    // period that is. `dimKey` rather than the label: a label is display text
    // and `selectMetricPeriods` reads the grain off the key's shape.
    // TWO ARRAYS, on purpose. The rule reads KEYS — a period's identity, and
    // the thing whose dates can be compared. The caption shows LABELS, which
    // are display text and routinely differ: Budget Period is keyed
    // `2026-08-01` and labelled `Aug 2026`. `DimValue` carries both so these
    // two jobs never get confused, and rendering the key was doing exactly
    // that.
    const periodKeys = trended ? view.rowKeys.map((rowKey) => rowKey[0]?.key ?? '') : undefined;
    const periodLabels = trended
      ? view.rowKeys.map((rowKey) => rowKey[0]?.label ?? rowKey[0]?.key ?? '')
      : undefined;
    const grain = grainOfDimension(spec.rows[0] ?? '');
    const todayIso = (today ?? new Date()).toISOString().slice(0, 10);
    const selection =
      trended && periodKeys && !comparison
        ? selectMetricPeriods({
            keys: periodKeys,
            // ASKED OF THE DIMENSION, not guessed from the key's punctuation.
            // Budget Period is a month written as a date, and reading it as a
            // day made two consecutive budget months non-adjacent — so a
            // budgets metric offered no comparison at all.
            grain,
            selectedFrom: selectedPeriod?.from ?? spec.from,
            selectedTo: selectedPeriod?.to ?? spec.to,
            today: todayIso,
          })
        : null;
    // Drawn as an AREA regardless of what the spec says, because a sparkline
    // is one shape and the spec's own `viz` is `metric` — which is not a chart
    // kind at all. `compact` is what strips the axes, grid, legend and dots
    // that a full chart would carry into a box this size.
    const drawn =
      adapter && trended
        ? pivotToChartSpec(model, spec, view, undefined, { kind: 'area', compact: true })
        : null;
    // TRIMMED TO THE PERIOD BEING REPORTED. The query reaches past it — by a
    // partial period in the default case, because the rule above refuses to
    // report one — and plotting what the number deliberately excludes puts a
    // plunge to the floor beside a healthy figure. The two halves of the tile
    // then contradict each other, and a reader believes the chart.
    //
    // In to-date mode `current` is already the last period, so this keeps
    // everything: the partial bucket IS what is being reported there.
    // THE NUMBER DRILLS INTO THE PERIOD IT REPORTS — the one grain the figure
    // is, never the history the query reached back for. With no trend it is
    // the whole window, which is exactly what an untrended figure totals.
    // Against a whole-window comparison there is no one grain: the figure is
    // the window, and the host hands the drill that window's question.
    const metricDrill =
      onDrill && measureId && (!trended || selection || comparison)
        ? (el: HTMLElement, text: string) => {
            const rowKey = selection ? view.rowKeys[selection.current] : undefined;
            const mode = showAsFor(spec, measureId);
            onDrill({
              at: cellCoordinate(spec, rowKey, undefined),
              labels: coordinateLabels(spec, rowKey, undefined),
              measureId,
              text,
              ...(mode === 'raw' ? {} : { showAs: mode }),
              anchor: () => (el.isConnected ? el.getBoundingClientRect() : null),
              returnFocus: el,
            });
          }
        : undefined;
    //
    // NOT TRIMMED against a whole-window comparison: the figure there covers
    // the running period too, so the chart shows every point of the period
    // and its mirror, the unfinished one named as such on hover.
    const kept = drawn && selection ? selection.current + 1 : drawn?.categories.length;
    const sparkSpec = drawn
      ? {
          ...drawn,
          categories: drawn.categories.slice(0, kept).map((category) => {
            // What the hover names: `Aug 2026`, not the key `2026-08` — and
            // an unfinished period says so, so its dip is not read as a drop.
            const key = category.rowKey?.[0]?.key;
            const running = key != null && !isComplete(key, grain, todayIso);
            return {
              ...category,
              label: `${readablePeriod(category.label)}${running ? ' · to date' : ''}`,
            };
          }),
          series: drawn.series.map((one) => ({ ...one, points: one.points.slice(0, kept) })),
        }
      : null;
    return (
      <>
        <TruncationBanner result={result} view={view} plan={plan} />
        <MetricValue
          model={model}
          spec={spec}
          result={result}
          series={series}
          periods={periodLabels}
          selection={selection}
          sparkline={adapter && sparkSpec ? adapter(sparkSpec) : undefined}
          previous={previous}
          comparison={comparison}
          heading={heading}
          measureNamed={measureNamed}
          periodNamed={periodNamed}
          onDrill={metricDrill}
        />
      </>
    );
  }

  if (spec.viz !== 'table') {
    const drawn = adapter && pivotToChartSpec(model, spec, view);
    // A mark drills as the cell behind it would in the table.
    const chart =
      drawn && onDrill
        ? {
            ...drawn,
            onPointClick: (point: Parameters<NonNullable<ChartSpec['onPointClick']>>[0]) => {
              const cell = chartDrillCell(
                spec,
                drawn,
                point,
                {
                  // Read off the chart's LIVE box, so the menu stays by the
                  // mark when the page scrolls under it.
                  anchor: () => {
                    const box = chartBox.current;
                    if (!box?.isConnected) return null;
                    const r = box.getBoundingClientRect();
                    const at = pressedAt.current;
                    return at ? new DOMRect(r.left + at.x, r.top + at.y, 0, 0) : r;
                  },
                  returnFocus: chartBox.current,
                },
                view,
              );
              if (cell) onDrill(cell);
            },
          }
        : drawn;
    // FALL THROUGH TO THE TABLE rather than blanking. A host that forgot the
    // provider, or a shape the translation declined to draw, still has numbers
    // worth reading — and a table nobody asked for is a far better failure
    // than an empty rectangle with no message in it.
    // A SIZED BOX, always. Renderers that lay out by measuring their parent
    // (Recharts' ResponsiveContainer, and most others) collapse to nothing
    // inside an auto-height flex child — silently, drawing a correct chart at
    // zero pixels. Giving every adapter the same box makes that this file's
    // problem once rather than each adapter's problem forever.
    if (adapter && chart)
      return (
        <>
          <TruncationBanner result={result} view={view} plan={plan} />
          <OmittedMeasures chart={chart} />
          <div
            ref={chartBox}
            className="rk-chart"
            onPointerDown={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              pressedAt.current = { x: e.clientX - r.left, y: e.clientY - r.top };
            }}
          >
            {adapter(chart)}
          </div>
        </>
      );
  }

  return (
    <>
      <TruncationBanner result={result} view={view} plan={plan} />
      <PivotTable
        model={model}
        spec={spec}
        view={view}
        onSort={onSort}
        onToggleGroup={onToggleGroup}
        onCollapseLevel={onCollapseLevel}
        onDrill={onDrill}
        truncated={result.meta.truncated}
        formulas={result.meta.formulas}
      />
    </>
  );
}

/**
 * The measures on Values this chart could not put on its axis, NAMED.
 *
 * A measure left off a chart and a measure that was never added look exactly
 * alike — a legend without it — and only one of them is fixed by switching to
 * a table. The chart used to drop every measure after the first this way, with
 * the Values shelf beside it still listing them.
 */
function OmittedMeasures({ chart }: { chart: ChartSpec }) {
  const omitted = chart.omittedMeasures ?? [];
  if (omitted.length === 0) return null;
  const one = omitted.length === 1;
  return (
    <p className="rk-chart-omitted" role="status">
      <strong>{omitted.join(', ')}</strong> {one ? 'is' : 'are'} not drawn: a chart has one axis,
      and {one ? 'it reads' : 'they read'} in a different unit from {chart.measureLabel}. Show this
      as a table to see every measure.
    </p>
  );
}

/**
 * Said before the numbers, because it is about all of them.
 *
 * A pivot that hit its ceiling reports over PART of its answer, and every
 * figure under it — each cell, each subtotal, the grand total — is computed
 * from that part. The footer used to carry this as a 10px pill between the row
 * count and the formula list, which is where a reader looks last if at all:
 * the Clientwise utilization report was read, exported and circulated at 69% of
 * the month with that pill on screen the whole time.
 *
 * So: above the grid, in the colour the product uses for "attend to this", and
 * phrased as what it costs rather than as what the engine did.
 *
 * PIVOTS ONLY. A listing's `truncated` means "there are further pages", which
 * the pager already says and which asks nothing of the reader; the caller
 * reaches this only from the pivot, chart and metric branches.
 */
function TruncationBanner({
  result,
  view,
  plan,
}: {
  result: QueryResult;
  view: PivotView;
  plan?: QueryPlan;
}) {
  // EITHER cap. The projection's own — `spec.limit` taken after the ranking —
  // is invisible to the server, so `meta.truncated` alone stays quiet through
  // it. See {@link PivotView.rowsDropped}.
  if (!result.meta.truncated && !view.rowsDropped) return null;
  // THREE STATES, not two, and the third is the one that used to lie. When SQL
  // owned the order the rows shown are the right ones and there are simply more
  // below. When the PROJECTION owns it the query stopped before this order
  // existed, so the rows shown are an arbitrary slice — "the top of the report"
  // is the one thing it does not mean. And WITHOUT A PLAN neither is known: a
  // dashboard tile mounts this with no plan at all, where defaulting to the SQL
  // wording stated as fact that there were more rows below, on exactly the
  // shape where that is false. Say only what holds either way.
  const rankedOverPartial = plan && plan.memory.sort.some((s) => s.axis === 'rows');
  const groups = result.meta.rowCount.toLocaleString('en-US');
  return (
    <p className="rk-partial" role="status">
      <strong>This is part of the report, not all of it.</strong>{' '}
      {view.rowsDropped
        ? `This report caps itself at ${view.rowKeys.length.toLocaleString('en-US')} rows and the answer had more, so the rest were dropped after ranking.`
        : rankedOverPartial === undefined
          ? `The query stopped at ${groups} groups.`
          : rankedOverPartial
            ? `The query stopped at ${groups} groups before these rows were ranked, so the rows shown are an arbitrary part of the answer.`
            : `The query stopped at ${groups} groups and there were more below.`}{' '}
      Every total here covers only what is shown. Narrow the period or add a filter, then read the
      totals.
    </p>
  );
}

/**
 * The definitions strip: what produced these numbers, and whether you are
 * seeing all of them.
 *
 * Fed from `meta`, never from the local model copy — so a formula the server
 * changed shows up here without a client deploy, and truncation cannot be
 * silent (FR-1.14, FR-1.16).
 */
/** One measure's disclosed definition. See {@link definitionsFor}. */
export interface MeasureDefinition {
  id: string;
  label: string;
  /** What the number means, as the server or the model states it. */
  text: string;
  /** What it is composed of, in the model's own vocabulary. */
  formula?: string;
}

/**
 * The ƒ definitions a report discloses, one per measure it shows.
 *
 * ONE resolution for every surface that discloses them — the footer strip and
 * a dashboard metric's ⓘ — so a tile and a table showing the same measure
 * cannot state it differently.
 */
export function definitionsFor(
  model: ReportModel | undefined,
  spec: ReportSpec | undefined,
  result: QueryResult,
): MeasureDefinition[] {
  // A listing's numbers are named in `columns`, mixed in among its dimensions —
  // and a dimension has no formula to disclose, so the ones that resolve to a
  // measure are what this strip is about.
  const named = spec
    ? spec.kind === 'detail'
      ? spec.columns
      : spec.measures
    : Object.keys(result.meta.formulas);
  // `meta` first, so a formula the server changed shows up without a client
  // deploy; the model fills in anything the transport did not send.
  //
  // `formula` — the composition in the model's own vocabulary — has no `meta`
  // half to prefer, and needs none: the catalog it rides on is fetched from the
  // server per session rather than bundled, so it is already as fresh as `meta`
  // is. It is absent for a measure that is not composed of others, and for
  // every measure when the caller passed no model.
  return named.flatMap((id) => {
    const measure = model ? findMeasure(model, id) : undefined;
    const text = result.meta.formulas[id] ?? measure?.formulaText;
    if (text === undefined) return [];
    return [{ id, label: measure?.label ?? id, text, formula: measure?.formula }];
  });
}

export function ReportFooter({
  model,
  spec,
  result,
  plan,
  summary = true,
}: {
  model?: ReportModel;
  spec?: ReportSpec;
  result: QueryResult;
  /**
   * The row count and the ƒ definitions — the footer's always-there half.
   *
   * `false` for a metric tile: its "rows" are the periods of its own history,
   * which says nothing to a reader, and its host shows the definitions in the
   * header instead. The WARNINGS stay either way — a capped or unanswerable
   * number must say so whatever it is drawn as — and with neither, the footer
   * renders nothing rather than an empty rule.
   */
  summary?: boolean;
  /**
   * Which axes the projection ordered, and what it capped.
   *
   * Needed to tell the two KINDS of truncation apart (see below), and to see
   * the projection's OWN cap at all: `spec.limit` is taken after the ranking,
   * so `meta.truncated` is false through it. Without this the footer printed a
   * clean row count beside a grid whose bottom row said "Total (partial)".
   */
  plan?: QueryPlan;
}) {
  const formulas = definitionsFor(model, spec, result).map(
    (d) => [d.id, d.label, d.text, d.formula] as const,
  );
  // A listing counts its whole answer, so the row count is the ANSWER's, not
  // this page's — the pager beside it says which slice is on screen. A pivot
  // has only what came back.
  //
  // Read off the KIND, not off the presence of a count. A page past the end has
  // no count to read — that is what makes it past the end — so keying on the
  // count sent exactly that page down the pivot's branch and printed "capped —
  // narrow the period" on a listing, telling the reader to change the question
  // when the only thing wrong was the page number.
  //
  // `pastEnd` carries the same answer for a caller that passes no spec: the
  // server says outright that this is a listing whose page ran off the end,
  // which is precisely the state the count-based fallback misreads.
  // THE SAME TWO CAPS THE BANNER READS. Re-projected rather than handed in: the
  // footer is placed by the host beside the view, not inside it, and the eager
  // half of `pivot()` is one pass over the rows — the layout walk, which is the
  // expensive part, is lazy and never asked for here.
  const rowsDropped = useMemo(() => {
    if (!model || !spec || spec.kind === 'detail' || plan?.memory.limit === undefined) return false;
    return pivot(model, spec, result, plan.memory.sort, plan.memory.limit).rowsDropped;
  }, [model, spec, result, plan]);
  const partial = result.meta.truncated || rowsDropped;
  const pastEnd = result.meta.pastEnd === true;
  const paged = spec ? spec.kind === 'detail' : result.meta.totalRows !== undefined || pastEnd;
  const rowCount = result.meta.totalRows ?? result.meta.rowCount;
  const warns = (partial && !paged) || (result.meta.unusableSources?.length ?? 0) > 0;
  if (!summary && !warns) return null;

  return (
    <div className="rk-footer">
      {/* NO COUNT past the end, because there is none: an empty page carries no
          row to read the total off. Printing this page's 0 there reads as "the
          report is empty" — the one conclusion it must not support, and the
          reason `pastEnd` exists at all. The body says what happened. */}
      {summary && !pastEnd && <span>{rowCount.toLocaleString('en-US')} rows</span>}
      {partial &&
        // NOT for a listing. Its `truncated` means "there are further pages",
        // which the pager already says and which asks nothing of the reader —
        // the old "capped — narrow the period or add a filter to see the rest"
        // was telling them to change the question to be allowed to finish the
        // answer. A pivot still cannot page, so its cap is still a cap.
        !paged &&
        // TWO KINDS OF CAPPED, and conflating them is how a wrong number gets
        // read as a complete one. When SQL owned the order, the rows shown are
        // the right ones and there are simply more below. When the PROJECTION
        // owns it, the query stopped at its ceiling before this order existed —
        // so the ranking was computed over whatever the ceiling happened to
        // keep, and "there is more below" is the one thing it does not mean.
        // WARNING-COLOURED, and it says the same thing the banner above the
        // grid says. It used to be the only place truncation was mentioned, in
        // the footer's muted 10px beside the row count, where it read as a note
        // about ordering rather than as "these numbers are incomplete".
        // THREE STATES, as in the banner: with no plan — which is how a
        // dashboard tile mounts this — neither reading is known, and the
        // SQL-ordered wording asserts something false about a memory-ordered
        // cap. The neutral text is what both truncations have in common.
        (plan === undefined ? (
          <span
            className="rk-notice rk-notice-warn"
            title="The query stopped at its ceiling, so every total covers only the rows shown"
          >
            partial — every total covers only the rows shown
          </span>
        ) : plan.memory.sort.some((s) => s.axis === 'rows') ? (
          <span
            className="rk-notice rk-notice-warn"
            title="The query stopped at its ceiling before these rows were ranked, so the rows shown are an arbitrary part of the answer and every total covers only them"
          >
            partial — ranked over part of the answer
          </span>
        ) : (
          <span
            className="rk-notice rk-notice-warn"
            title="The query returned more groups than the ceiling allows, so every total covers only the rows shown"
          >
            partial — there are more rows below
          </span>
        ))}
      {/* An em dash from a category error reads exactly like an em dash from
          missing data, and only the server can tell them apart. Grouping by
          Customer makes every utilization percentage blank because capacity
          belongs to a person, not to an engagement — worth one sentence rather
          than a column the reader assumes is broken. */}
      {result.meta.unusableSources?.length ? (
        <span
          className="rk-notice"
          title="These measures are blank because the question has no answer at this grouping, not because data is missing"
        >
          some measures cannot be split this way — their cells are blank
        </span>
      ) : null}
      {summary && formulas.length > 0 && (
        <details className="rk-formulas">
          <summary>
            {/* The same icon as every column header's hint, so the footer
                reads as "all of those, at once" rather than a different
                thing spelled with a letter. */}
            <FormulaIcon size={12} className="rk-formulas-icon" /> definitions
          </summary>
          <dl>
            {formulas.map(([measureId, name, text, formula]) => (
              <div key={measureId}>
                <dt>{name}</dt>
                <dd>{text}</dd>
                {/* The sentence says what the number MEANS; this says what it
                    is made of, and every name in it is a measure the same
                    picker offers — so a reader who does not recognise one can
                    go and read its definition too. */}
                {formula && <dd className="rk-formula">{formula}</dd>}
              </div>
            ))}
          </dl>
        </details>
      )}
    </div>
  );
}
